"use client";

import SearchIcon from "@/components/core/icons/SearchIcon";
import { useCommandK } from "@/helpers/CommandK";
import { getBreadcrumbs } from "@/lib/navigation/get-navigation";
import { apiReferenceHref, methods } from "@/lib/openapi";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { useRouter } from "next/navigation";
import React from "react";
import clsx from "clsx";
import {
  isNavigationLink,
  isNavigationLinkGroup,
  type SidebarNavigationStructure,
} from "./navigation";
import type { NavigationRoute } from "./SideNavigation";

type TopNavTab = { title: string; href: string };

const leftTabs: TopNavTab[] = [
  { title: "Dash0", href: "/docs/dash0" },
  { title: "OTelBin", href: "/docs/otelbin" },
  { title: "API Reference", href: "/docs/api-reference" },
];

export default function TopNavigation({
  routes,
  defaultStructure,
}: {
  routes?: NavigationRoute[];
  defaultStructure: SidebarNavigationStructure;
}) {
  const { show } = useCommandK();
  const pathname = usePathname();
  const router = useRouter();

  const structure = useMemo(() => {
    if (routes) {
      for (const route of routes) {
        if (pathname.startsWith(route.pathPrefix)) {
          return route.structure;
        }
      }
    }
    return defaultStructure;
  }, [pathname, routes, defaultStructure]);

  const breadcrumbTrail = useMemo(() => {
    if (
      pathname === apiReferenceHref ||
      pathname.startsWith(`${apiReferenceHref}/`)
    ) {
      const root = { title: "API reference", href: apiReferenceHref };
      try {
        const [kind, ...parts] = pathname
          .slice(apiReferenceHref.length + 1)
          .split("/")
          .map(decodeURIComponent);
        if (kind === "schemas" && parts.length) {
          return [
            root,
            { title: "Schemas" },
            { title: parts.join("/"), href: pathname },
          ];
        }
        if (
          kind === "operations" &&
          methods.includes(parts[0]) &&
          parts.length > 1
        ) {
          return [
            root,
            { title: "Endpoints" },
            {
              title: `${parts[0].toUpperCase()} /${parts.slice(1).join("/")}`,
              href: pathname,
            },
          ];
        }
      } catch {
        // Malformed URL escapes must not break the shared navigation.
      }
      return [root];
    }
    const trail = getBreadcrumbs(pathname, structure);
    // Also try with trailing slash stripped
    if (trail.length === 0 && pathname.endsWith("/")) {
      return getBreadcrumbs(pathname.slice(0, -1), structure);
    }
    return trail;
  }, [pathname, structure]);

  return (
    <nav className="w-full border-b border-b-grey-850 relative flex items-center justify-between gap-2 lg:gap-8 z-10 lg:overflow-visible px-2 lg:px-0 overflow-y-visible overflow-x-auto custom-scrollbar">
      <div className="flex lg:gap-6 items-center w-full">
        <div className="flex items-center gap-2 -ml-2 lg:w-[330px] shrink-0">
          {leftTabs.map((tab) => (
            <Link
              href={tab.href}
              onMouseEnter={() => router.prefetch(tab.href)}
              data-nav-tab
              aria-current={pathname.startsWith(tab.href) ? "page" : undefined}
              key={tab.href}
              className={clsx(
                "relative leading-none p-2 text-sm rounded font-medium mb-2 transition-colors duration-150",
                pathname.startsWith(tab.href) &&
                  "after:absolute after:-bottom-2 after:left-2 after:right-2 after:h-px after:bg-white",
                !pathname.startsWith(tab.href) && "lg:hover:bg-grey-850",
              )}
            >
              {tab.title}
            </Link>
          ))}
        </div>
        <div className="w-full h-full mb-2 items-center gap-1 hidden lg:flex">
          {breadcrumbTrail.length > 0 &&
            breadcrumbTrail.map((item, index) => {
              const href = isNavigationLink(item)
                ? item.href
                : isNavigationLinkGroup(item) && item.href
                  ? item.href
                  : undefined;

              return (
                <React.Fragment key={`br-${index}`}>
                  {href ? (
                    <Link
                      href={href}
                      className={clsx(
                        "font-medium text-sm truncate line-clamp-1",
                        index < breadcrumbTrail.length - 1
                          ? "text-grey hover:text-white transition-colors duration-150"
                          : "text-white",
                      )}
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <span
                      className={clsx(
                        "font-medium text-sm truncate line-clamp-1",
                        index < breadcrumbTrail.length - 1
                          ? "text-grey"
                          : "text-white",
                      )}
                    >
                      {item.title}
                    </span>
                  )}
                  {index < breadcrumbTrail.length - 1 && (
                    <span className="text-grey-600">/</span>
                  )}
                </React.Fragment>
              );
            })}
        </div>
      </div>
      <div className="flex flex-row-reverse lg:flex-row items-center gap-2 -mr-2 shrink-0 mb-2">
        <button
          onClick={show}
          className="bg-grey-850 group hidden lg:flex w-auto rounded pl-1.5 p-1 lg:mr-4 gap-2 items-center max-[319px]:justify-center max-[319px]:aspect-square max-[319px]:gap-2 justify-between text-grey transition-colors duration-150 hover:text-grey-400 hover:bg-grey-800"
        >
          <div className="shrink-0 h-4 aspect-square flex items-center justify-center">
            <SearchIcon />
          </div>
          <div className="flex items-center gap-0 max-lg:hidden p-1 bg-grey-800 rounded-sm group-hover:bg-grey-700 transition-colors duration-100 group-hover:text-white">
            <p className="leading-none text-[14px] font-medium">⌘K</p>
          </div>
        </button>
      </div>
    </nav>
  );
}
